import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { ApolloError, useReactiveVar } from '@apollo/client';

import {
  useErpAiStudentFinancePortraitQuery,
  useErpAiStudentPortraitQuery,
  useErpRefreshAiStudentPortraitMutation,
} from 'gqlhooks';
import { userStateVar } from 'core/state/userState';
import { GlobalToastTypeNames, ToastTypes, UserRole } from 'types';

import { aiAgentErrorCode } from '../AiAgents/aiAgentPlatformModel';
import { LEAD_ROLES } from '../Crm/useLeadPermissions';
import { useAiPollGuard } from '../AiAgents/useAiPollGuard';
import { TASK_TRACKER_ROLES } from '../Palette/paletteRoles';
import { useStudentPermissions } from '../Students/useStudentPermissions';
import { useToasts } from '../Toast';
import { useConditionalPolling } from '../Zoom/useConditionalPolling';
import {
  AI_PORTRAIT_POLL_MS,
  AiStudentFinancePortrait,
  AiStudentPortrait,
  aiPortraitPolled,
  IAiEvidenceAccess,
} from './aiPortraitModel';

export interface IAiPortraitQueryOptions {
  /** Нет права (`canSeePortrait`) или блок закрыт — запрос не уходит. */
  skip?: boolean;
}

export interface IAiStudentPortraitState {
  portrait: AiStudentPortrait | null;
  /** Ответа ещё нет. */
  loading: boolean;
  error?: ApolloError;
  /** Идёт опрос: портрет в очереди или пишется (и ещё не завис). */
  polling: boolean;
  /**
   * Статус «в движении» дольше `AI_POLL_GUARD_MS` — опрос остановлен
   * (`useAiPollGuard`), экран показывает «trwa dłużej niż zwykle».
   */
  stalled: boolean;
  refetch: () => void;
}

/**
 * ТЗ 0104 — портрет студента (`aiStudentPortrait`) для полной панели
 * карточки и раздела мини-карточки (один документ, одна запись кеша по
 * `studentId`).
 *
 * Пока портрет в очереди или пишется — опрос раз в 4 с, только в видимой
 * вкладке (`useConditionalPolling`). `cache-and-network`: открытая заново
 * карточка сразу рисует то, что в кеше, и тут же узнаёт свежий статус и
 * признак «устарел». Портрет «в движении» дольше получаса — опрос
 * останавливается (`useAiPollGuard`, `stalled`).
 */
export const useAiStudentPortrait = (
  studentId: string | null | undefined,
  { skip = false }: IAiPortraitQueryOptions = {},
): IAiStudentPortraitState => {
  const active = Boolean(studentId) && !skip;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useErpAiStudentPortraitQuery({
    variables: { studentId: studentId as string },
    skip: !active,
    fetchPolicy: 'cache-and-network',
  });

  const portrait = data?.aiStudentPortrait ?? null;
  const { polling, stalled } = useAiPollGuard(
    active && aiPortraitPolled(portrait?.status),
    String(studentId ?? ''),
  );

  useConditionalPolling({
    startPolling,
    stopPolling,
    pollInterval: AI_PORTRAIT_POLL_MS,
    predicate: polling,
  });

  const reload = useCallback(() => {
    if (!active) return;
    refetch().catch(() => undefined);
  }, [active, refetch]);

  return {
    portrait,
    loading: active && loading && !data,
    error: active ? error : undefined,
    polling,
    stalled,
    refetch: reload,
  };
};

export interface IAiFinancePortraitOptions extends IAiPortraitQueryOptions {
  /**
   * Опрашивать вместе с портретом: финансовое приложение пишет тот же
   * прогон, и его статус — статус этого прогона.
   */
  follow?: boolean;
}

export interface IAiStudentFinancePortraitState {
  /** `null` — приложения нет у роли (сервер) или ещё нет ответа. */
  finance: AiStudentFinancePortrait | null;
  loading: boolean;
  error?: ApolloError;
  refetch: () => void;
}

/**
 * ТЗ 0104 — финансовое приложение портрета (`aiStudentFinancePortrait`).
 * Только финансовым ролям (`canSeeFinancePortrait`): остальным — `skip`,
 * запрос не уходит (сервер им всё равно ответил бы `null`).
 *
 * Опрос — пока своё приложение в движении или пока опрашивается портрет
 * (`follow`); когда портрет остановился, приложение перечитывается ещё раз,
 * чтобы не застрять на последнем «пишется».
 */
export const useAiStudentFinancePortrait = (
  studentId: string | null | undefined,
  { skip = false, follow = false }: IAiFinancePortraitOptions = {},
): IAiStudentFinancePortraitState => {
  const active = Boolean(studentId) && !skip;
  const {
    data,
    loading,
    error,
    refetch,
    startPolling,
    stopPolling,
  } = useErpAiStudentFinancePortraitQuery({
    variables: { studentId: studentId as string },
    skip: !active,
    fetchPolicy: 'cache-and-network',
  });

  const finance = data?.aiStudentFinancePortrait ?? null;
  // Своё приложение «в движении» без портрета — тот же предохранитель.
  const own = useAiPollGuard(
    active && aiPortraitPolled(finance?.status),
    String(studentId ?? ''),
  );

  useConditionalPolling({
    startPolling,
    stopPolling,
    pollInterval: AI_PORTRAIT_POLL_MS,
    predicate: active && (follow || own.polling),
  });

  const reload = useCallback(() => {
    if (!active) return;
    refetch().catch(() => undefined);
  }, [active, refetch]);

  // Портрет перестал опрашиваться (готов или упал) — последний взгляд на
  // приложение: его опрос мог разминуться с концом прогона на один такт.
  const followedRef = useRef(follow);
  useEffect(() => {
    if (followedRef.current && !follow) reload();
    followedRef.current = follow;
  }, [follow, reload]);

  return {
    finance,
    loading: active && loading && !data,
    error: active ? error : undefined,
    refetch: reload,
  };
};

export type AiPortraitRefreshResult =
  | { ok: true; status: string }
  | {
      ok: false;
      /** `extensions.code` отказа (`budget_exceeded`…) или `generic`. */
      code: string;
    };

/**
 * ТЗ 0104 — «Odśwież portret» (`refreshAiStudentPortrait`). Платный вызов
 * модели: подтверждение — на экране, до вызова.
 *
 * Ответ (`queued`) ложится в кеш по `studentId` — панель видит статус и
 * начинает опрос сама. Отказ возвращается кодом: экран объясняет его рядом
 * с кнопкой; успех — тостом.
 */
export const useRefreshAiStudentPortrait = (): {
  refresh: (studentId: string) => Promise<AiPortraitRefreshResult>;
  pending: boolean;
} => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [mutate, { loading }] = useErpRefreshAiStudentPortraitMutation();

  const refresh = useCallback(
    async (studentId: string): Promise<AiPortraitRefreshResult> => {
      try {
        const { data } = await mutate({ variables: { studentId } });
        const portrait = data?.refreshAiStudentPortrait;
        if (!portrait) return { ok: false, code: 'generic' };
        addToast({
          type: GlobalToastTypeNames.AiAgents,
          data: {
            type: ToastTypes.success,
            title: t('aiPortrait.toast.queued'),
          },
        });
        return { ok: true, status: portrait.status };
      } catch (error) {
        return { ok: false, code: aiAgentErrorCode(error) ?? 'generic' };
      }
    },
    [mutate, addToast, t],
  );

  return { refresh, pending: loading };
};

/**
 * Что из ссылок доказательств открывается у этого сотрудника — маршрут
 * таск-трекера, раздел «Лиды» (ТЗ 0120) и вкладки карточки студента
 * (`aiEvidenceHref`).
 */
export const useAiEvidenceAccess = (): IAiEvidenceAccess => {
  const userState = useReactiveVar(userStateVar);
  const {
    canSeeAgreementsTab,
    canSeeCommunicationTab,
    canSeeInvoicesTab,
    canSeePaymentsTab,
    canSeeSurveysTab,
  } = useStudentPermissions();
  const roles = (userState.me?.roles ?? []) as UserRole[];
  const canOpenTasks = roles.some((role) => TASK_TRACKER_ROLES.includes(role));
  const canOpenLeads = roles.some((role) => LEAD_ROLES.includes(role));
  return useMemo(
    () => ({
      canOpenTasks,
      canOpenLeads,
      canSeeAgreementsTab,
      canSeeCommunicationTab,
      canSeeInvoicesTab,
      canSeePaymentsTab,
      canSeeSurveysTab,
    }),
    [
      canOpenTasks,
      canOpenLeads,
      canSeeAgreementsTab,
      canSeeCommunicationTab,
      canSeeInvoicesTab,
      canSeePaymentsTab,
      canSeeSurveysTab,
    ],
  );
};
